
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, FileCheck, Clock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export const TestHistory = ({ candidateId }: { candidateId?: string }) => {
  const { data: testResponses, isLoading } = useQuery({
    queryKey: ['testResponses', candidateId],
    queryFn: async () => {
      if (!candidateId) return [];
      const { data, error } = await supabase
        .from('test_responses')
        .select(`
          *,
          tests (
            title,
            description
          )
        `)
        .eq('candidate_id', candidateId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!candidateId,
  });

  const isMobile = useIsMobile();

  if (isLoading) {
    return <div className="text-foreground">Loading test history...</div>;
  }

  if (!testResponses || testResponses.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        You haven't taken any tests yet.
      </div>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold mb-4 text-foreground">Test History</h2>
        {testResponses.map((response) => (
          <Card key={response.id} className="overflow-hidden">
            <CardContent className="p-4">
              <h3 className="font-medium text-foreground mb-2 line-clamp-1">
                {response.tests?.title || "Untitled Test"}
              </h3>
              
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="h-4 w-4" />
                  <span>{new Date(response.created_at).toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>
                    {response.completed_at 
                      ? `${Math.round((new Date(response.completed_at).getTime() - new Date(response.started_at).getTime()) / (1000 * 60))} mins` 
                      : "In progress"}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <Badge variant={response.status === "completed" ? "default" : "outline"} className="capitalize">
                  {response.status}
                </Badge>
                {response.status === "completed" && response.score !== null && (
                  <div className="flex items-center gap-1">
                    <FileCheck className="h-4 w-4 text-foreground" />
                    <span className="font-medium text-foreground">{response.score}%</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop table view
  return (
    <div>
      <h2 className="text-xl font-semibold mb-4 text-foreground">Test History</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-muted-foreground">Test</TableHead>
            <TableHead className="text-muted-foreground">Status</TableHead>
            <TableHead className="text-muted-foreground">Score</TableHead>
            <TableHead className="text-muted-foreground">Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {testResponses.map((response) => (
            <TableRow key={response.id}>
              <TableCell className="text-foreground">{response.tests?.title}</TableCell>
              <TableCell className="capitalize text-foreground">{response.status}</TableCell>
              <TableCell className="text-foreground">
                {response.score !== null ? `${response.score}%` : 'N/A'}
              </TableCell>
              <TableCell className="text-foreground">
                {new Date(response.created_at).toLocaleDateString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
